<template>
  <tr>
    <td width="10%">{{ playground.name }}</td>
    <td width="20%">{{ playground.id }}</td>
    <td width="30%">
      <v-btn
        v-if="playground.uuid_token"
        @click="clipboardCopy(playground.uuid_token)"
        icon
      >
        <v-icon>mdi-content-copy</v-icon>
      </v-btn>
      {{ playground.uuid_token }}
      <v-dialog
        v-if="playground.uuid_token"
        v-model="dialogTokenRefresh"
        persistent
        max-width="550px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon>
            <v-icon>mdi-trash-can-outline</v-icon>
          </v-btn>
        </template>
        <v-card tile>
          <v-card-title>
            <span class="headline">{{ $t("controlTokenDelete") }}</span>
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="green darken-1"
              text
              @click="
                refreshToken({ playground_id: playground.id }),
                  (dialogTokenRefresh = false)
              "
              >{{ $t("btnCreate") }}</v-btn
            >
            <v-btn
              color="red darken-1"
              text
              @click="dialogTokenRefresh = false"
              >{{ $t("btnCancel") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-else v-model="dialogTokenCreate" persistent max-width="550px">
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon>
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <v-card tile>
          <v-card-title>
            <span class="headline">{{ $t("controlTokenCreate") }}</span>
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="green darken-1"
              text
              @click="
                refreshToken({ playground_id: playground.id }),
                  (dialogTokenCreate = false)
              "
              >{{ $t("btnCreate") }}</v-btn
            >
            <v-btn
              color="red darken-1"
              text
              @click="dialogTokenCreate = false"
              >{{ $t("btnCancel") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </td>
    <td
      width="15%"
      :class="
        moment
          .parseZone()
          .isBefore(moment(playground.tech_sessions_access_expires_at))
          ? 'green--text'
          : 'red--text'
      "
      class="capitalize"
    >
      {{
        moment
          .parseZone()
          .isBefore(moment(playground.tech_sessions_access_expires_at))
          ? moment(playground.tech_sessions_access_expires_at).fromNow("mm")
          : $t("controlTableDisabled")
      }}
    </td>
    <td width="15%">
      <v-dialog
        v-if="playground.tech_control"
        v-model="dialogAllow"
        persistent
        max-width="550px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon>
            <v-icon>mdi-play-circle</v-icon>
          </v-btn>
        </template>
        <v-card tile>
          <v-card-title>
            <span class="headline">{{ $t("controlTableStart") }}</span>
          </v-card-title>
          <v-card-text>
            <v-text-field
              :label="$t('controlTableTime') + '*'"
              name="minutes"
              type="text"
              required
              v-model.number="newTechSession.minutes"
              :error-messages="sessionMinutesErrors"
              @input="$v.newTechSession.minutes.$touch()"
              @blur="$v.newTechSession.minutes.$touch()"
            ></v-text-field>
            <v-text-field
              :label="$t('controlTableComment')"
              name="comment"
              type="text"
              required
              v-model="newTechSession.comment"
            ></v-text-field>
            <small>* {{ $t("validation") }}</small>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="green darken-1"
              text
              @click="
                startTechSession({ id: playground.id, data: newTechSession }),
                  (dialogAllow = false)
              "
              :disabled="$v.$invalid"
              >{{ $t("btnStart") }}</v-btn
            >
            <v-btn color="red darken-1" text @click="dialogAllow = false">{{
              $t("btnCancel")
            }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-if="
          playground.tech_control &&
          moment
            .parseZone()
            .isBefore(moment(playground.tech_sessions_access_expires_at))
        "
        v-model="dialogDisallow"
        max-width="550px"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon>
            <v-icon>mdi-stop-circle</v-icon>
          </v-btn>
        </template>
        <v-card tile>
          <v-card-title class="headline">{{
            $t("controlTableStop")
          }}</v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" text @click="dialogDisallow = false">{{
              $t("btnNo")
            }}</v-btn>
            <v-btn
              color="green darken-1"
              text
              @click="stopTechSession(playground.id)"
              >{{ $t("btnYes") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </td>
  </tr>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, minValue } from "vuelidate/lib/validators";
import { mapActions } from "vuex";

export default {
  name: "ControlTable",

  mixins: [validationMixin],

  data() {
    return {
      dialogAllow: false,
      dialogTokenRefresh: false,
      dialogTokenCreate: false,
      dialogDisallow: false,
      newTechSession: {
        minutes: "",
        comment: "",
      },
    };
  },

  props: ["playground"],

  validations: {
    newTechSession: {
      minutes: {
        required,
        minValue: minValue(1),
      },
    },
  },

  computed: {
    sessionMinutesErrors() {
      const errors = [];
      if (!this.$v.newTechSession.minutes.$dirty) return errors;
      !this.$v.newTechSession.minutes.required &&
        errors.push(this.$i18n.t("validationRequired"));
      !this.$v.newTechSession.minutes.minValue &&
        errors.push(this.$i18n.t("validationMinValue"));
      return errors;
    },
  },

  methods: {
    ...mapActions([
      "allowSession",
      "disallowSession",
      "loadLocationsAll",
      "createToken",
    ]),
    startTechSession(data) {
      this.allowSession(data).then(() => {
        this.loadLocationsAll();
        this.dialogAllow = false;
      });
    },
    stopTechSession() {
      this.disallowSession(this.playground.id).then(() => {
        this.loadLocationsAll();
        this.dialogDisallow = false;
      });
    },
    refreshToken(data) {
      this.createToken(data).then(() => {
        this.loadLocationsAll();
      });
    },
    clipboardCopy: (text) => navigator.clipboard.writeText(text),
  },

  watch: {
    dialogAllow(visible) {
      if (!visible) {
        this.$v.$reset();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.capitalize::first-letter {
  text-transform: capitalize;
}
</style>
